/**
 * client.ts — barrel re-export of the per-feature api modules (PR-15 split
 * of the original 1311-line monolith).
 *
 * Every kid-app caller still writes `import { api, ApiCallError, type XxxRow }
 * from '../api/client'` — this file preserves that one-stop import surface
 * while the actual endpoint definitions live next to each other by feature
 * (auth.ts, pets.ts, shop.ts, …). New endpoints should be added to the
 * relevant feature file, not here.
 *
 * 调用关系：
 *   - Imported by virtually every page + component in frontend/src/.
 *   - Sibling api/scheduleClient.ts still imports `ApiCallError` from this
 *     barrel so the kid app has one error class across both modules.
 *   - Backend endpoints live in backend/src/routes/*.js — each per-feature
 *     module's docblock names the routes it owns.
 *
 * 关键约束：
 *   - The exported `api` object is a *value*, not a type — its keys must
 *     stay unique across the merged per-feature objects. If two features
 *     accidentally export the same method name, the later spread wins
 *     silently. To stay safe, the per-feature `*Api` objects are named
 *     by feature and reviewed at PR time.
 *   - Type re-exports keep `import type { ShopItem } from '../api/client'`
 *     working for the ~60 consumer files; rename a type only if you also
 *     do a cross-repo grep for the old name.
 *
 * 关联：frontend/src/api/_core.ts (shared call wrapper), scheduleClient.ts
 * (sibling feature), backend/src/routes/* (matching server handlers).
 */

// ─── Core helpers + error class — re-exported so consumers can still write
//    `import { ApiCallError, getToken, setSession, clearSession } from '../api/client'`.
export { ApiCallError, getToken, setSession, clearSession } from './_core';

// ─── Per-feature api fragments. Imported here, merged into one object below.
import { authApi } from './auth';
import { meApi } from './me';
import { petsApi } from './pets';
import { shopApi } from './shop';
import { economyApi } from './economy';
import { choresApi } from './chores';
import { journalApi } from './journal';
import { chatApi } from './chat';
import { companionApi } from './companion';
import { pushApi } from './push';
import { quizApi } from './quiz';
import { zooApi } from './zoo';
import { ritualsApi } from './rituals';
import { gamesApi } from './games';

// ─── The unified api surface. Spread order is alphabetical-by-feature; no
//    two features currently share a method name. If you add a new feature
//    module, append its `*Api` here.
export const api = {
  ...authApi,
  ...meApi,
  ...petsApi,
  ...shopApi,
  ...economyApi,
  ...choresApi,
  ...journalApi,
  ...chatApi,
  ...companionApi,
  ...pushApi,
  ...quizApi,
  ...zooApi,
  ...ritualsApi,
  ...gamesApi,
};

// ─── Type re-exports. Grouped by source module so it's obvious where to
//    look when one of these shapes changes. Every type below was exported
//    from the pre-split client.ts and is consumed by at least one page or
//    component file (see `git grep "from '../api/client'"`).
export type { MailMessage, ParentVaultForChild } from './me';
export type {
  BondGainResponse,
  BondLevelUp,
  BondUnlock,
  PetBondRow,
  PetPersona,
  PetWish,
  PokedexBreed,
} from './pets';
export type { InventoryItem, ShopItem, WishCheckResponse } from './shop';
export type {
  ChildChallenge,
  InvestmentAsset,
  InvestmentHolding,
  InvestmentMarket,
} from './economy';
export type { ChatMsg } from './chat';
export type { QuizItem } from './quiz';
export type {
  BuildingType,
  LandType,
  ZooBreeding,
  ZooBuilding,
  ZooLand,
  ZooOverview,
  ZooPet,
} from './zoo';
export type { PetOfflineEvent, RitualsClaimRes, RitualsTodayRes } from './rituals';
export type { GameFinishRes, GameTodayStat, GamesTodayRes, RewardBlocked } from './games';
